import React from 'react';
import { 
  CheckCircle, 
  Building2, 
  Car, 
  Gift, 
  Zap, 
  MapPin, 
  Settings,
  Clock,
  Target,
  DollarSign,
  Receipt
} from 'lucide-react';

const Step6Review = ({ formData, editingConfig }) => {
  const vehicleTypes = formData.vehicleTypes || {};
  const surge = formData.surge || {};
  const zones = formData.zones || {};
  const dynamicCouponRules = formData.dynamicCouponRules || [];
  const incentives = formData.incentives || {};
  const cancellationPolicy = formData.cancellationPolicy || {};
  const dynamicFees = formData.dynamicFees || {};
  const dynamicTaxes = formData.dynamicTaxes || {};

  const calculateFarePreview = (vehicleType, distance, time) => {
    const vt = vehicleTypes[vehicleType];
    if (!vt) return 0;

    let fare = vt.baseFare;
    
    // Add distance-based pricing
    if (vt.rangePricing) {
      for (const range of vt.rangePricing) {
        if (distance >= range.minDistance && (range.maxDistance === null || distance <= range.maxDistance)) {
          fare += distance * range.value;
          break;
        }
      }
    }
    
    // Add time-based pricing
    fare += time * vt.timeCostPerMin;
    
    // Apply min/max fare
    fare = Math.max(fare, vt.minFare);
    fare = Math.min(fare, vt.maxFare);
    
    return fare;
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-2">Review Configuration</h4>
        <p className="text-sm text-gray-600">
          Review all settings before {editingConfig ? 'updating' : 'creating'} the fare configuration.
        </p>
      </div>

      {/* Basic Information */}
      <div className="card">
        <div className="card-header">
          <h5 className="text-md font-medium text-gray-900 flex items-center">
            <Building2 className="h-4 w-4 mr-2" />
            Basic Information
          </h5>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Region Code</label>
              <p className="text-sm text-gray-900">{formData.regionCode || 'Not set'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Currency</label>
              <p className="text-sm text-gray-900">{formData.currency}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Status</label>
              <p className="text-sm text-gray-900">
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  formData.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {formData.isActive ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Types */}
      <div className="card">
        <div className="card-header">
          <h5 className="text-md font-medium text-gray-900 flex items-center">
            <Car className="h-4 w-4 mr-2" />
            Vehicle Types ({Object.keys(vehicleTypes).length})
          </h5>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            {Object.entries(vehicleTypes).map(([type, config]) => (
              <div key={type} className="border rounded-lg p-4">
                <h6 className="text-sm font-medium text-gray-900 mb-2">{type}</h6>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Base Fare:</span>
                    <span className="ml-1 font-medium">₹{config.baseFare}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Time Cost/Min:</span>
                    <span className="ml-1 font-medium">₹{config.timeCostPerMin}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Min Fare:</span>
                    <span className="ml-1 font-medium">₹{config.minFare}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Max Fare:</span>
                    <span className="ml-1 font-medium">₹{config.maxFare}</span>
                  </div>
                </div>
                <div className="mt-2">
                  <span className="text-gray-500 text-sm">Range Pricing:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {config.rangePricing?.map((range, index) => (
                      <span key={index} className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                        {range.minDistance}-{range.maxDistance || '∞'}km: ₹{range.value}/km
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fare Preview */}
      {Object.keys(vehicleTypes).length > 0 && (
        <div className="card">
          <div className="card-header">
            <h5 className="text-md font-medium text-gray-900 flex items-center">
              <span className="text-lg mr-2">₹</span>
              Fare Preview
            </h5>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 border rounded-lg">
                <h6 className="text-sm font-medium text-gray-900 mb-2">Short Trip (5km, 10min)</h6>
                <div className="space-y-1">
                  {Object.keys(vehicleTypes).slice(0, 3).map((type) => (
                    <div key={type} className="text-sm">
                      <span className="text-gray-500">{type}:</span>
                      <span className="ml-1 font-medium">₹{calculateFarePreview(type, 5, 10).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <h6 className="text-sm font-medium text-gray-900 mb-2">Medium Trip (15km, 25min)</h6>
                <div className="space-y-1">
                  {Object.keys(vehicleTypes).slice(0, 3).map((type) => (
                    <div key={type} className="text-sm">
                      <span className="text-gray-500">{type}:</span>
                      <span className="ml-1 font-medium">₹{calculateFarePreview(type, 15, 25).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="text-center p-4 border rounded-lg">
                <h6 className="text-sm font-medium text-gray-900 mb-2">Long Trip (30km, 45min)</h6>
                <div className="space-y-1">
                  {Object.keys(vehicleTypes).slice(0, 3).map((type) => (
                    <div key={type} className="text-sm">
                      <span className="text-gray-500">{type}:</span>
                      <span className="ml-1 font-medium">₹{calculateFarePreview(type, 30, 45).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dynamic Coupons */}
      {dynamicCouponRules.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h5 className="text-md font-medium text-gray-900 flex items-center">
              <Gift className="h-4 w-4 mr-2" />
              Dynamic Coupon Rules ({dynamicCouponRules.length})
            </h5>
          </div>
          <div className="card-body">
            <div className="space-y-2">
              {dynamicCouponRules.map((rule, index) => (
                <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div>
                    <span className="font-medium">{rule.code}</span>
                    <span className="text-gray-500 ml-2">- {rule.description}</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {rule.type === 'percentage' ? `${rule.value}%` : `₹${rule.value}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Driver Incentives & Penalties */}
      {(incentives.driverIncentives?.length > 0 || incentives.driverPenalties?.length > 0) && (
        <div className="card">
          <div className="card-header">
            <h5 className="text-md font-medium text-gray-900 flex items-center">
              <Target className="h-4 w-4 mr-2" />
              Driver Incentives & Penalties
            </h5>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h6 className="text-sm font-medium text-gray-900 mb-2">Incentives ({incentives.driverIncentives?.length || 0})</h6>
                <div className="space-y-1">
                  {incentives.driverIncentives?.map((incentive, index) => (
                    <div key={index} className="text-sm text-gray-600">
                      {incentive.type} {incentive.condition} → ₹{incentive.bonus}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h6 className="text-sm font-medium text-gray-900 mb-2">Penalties ({incentives.driverPenalties?.length || 0})</h6>
                <div className="space-y-1">
                  {incentives.driverPenalties?.map((penalty, index) => (
                    <div key={index} className="text-sm text-gray-600">
                      {penalty.type} {penalty.condition} → ₹{penalty.penalty}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fees & Taxes */}
      {(Object.keys(dynamicFees).length > 0 || Object.keys(dynamicTaxes).length > 0) && (
        <div className="card">
          <div className="card-header">
            <h5 className="text-md font-medium text-gray-900 flex items-center">
              <Receipt className="h-4 w-4 mr-2" />
              Fees & Taxes
            </h5>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h6 className="text-sm font-medium text-gray-900 mb-2">Dynamic Fees ({Object.keys(dynamicFees).length})</h6>
                <div className="space-y-1">
                  {Object.entries(dynamicFees).map(([name, fee]) => (
                    <div key={name} className="text-sm text-gray-600">
                      {name}: {fee.type === 'percentage' ? `${fee.value}%` : `₹${fee.value}`}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h6 className="text-sm font-medium text-gray-900 mb-2">Dynamic Taxes ({Object.keys(dynamicTaxes).length})</h6>
                <div className="space-y-1">
                  {Object.entries(dynamicTaxes).map(([name, tax]) => (
                    <div key={name} className="text-sm text-gray-600">
                      {name}: {tax.type === 'percentage' ? `${tax.value}%` : `₹${tax.value}`}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Surge Pricing */}
      {surge.enabled && surge.multipliers?.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h5 className="text-md font-medium text-gray-900 flex items-center">
              <Zap className="h-4 w-4 mr-2" />
              Surge Pricing ({surge.multipliers.length} multipliers)
            </h5>
          </div>
          <div className="card-body">
            <div className="space-y-2">
              {surge.multipliers.map((multiplier, index) => (
                <div key={index} className="text-sm text-gray-600">
                  {multiplier.timeRange.start} - {multiplier.timeRange.end} on {multiplier.days.join(', ')}: 
                  {multiplier.type === 'multiplier' ? ` ${multiplier.value}x` : ` +₹${multiplier.value}`}
                  {multiplier.zones.length > 0 && ` (${multiplier.zones.join(', ')})`}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Zones */}
      {Object.keys(zones).length > 0 && (
        <div className="card">
          <div className="card-header">
            <h5 className="text-md font-medium text-gray-900 flex items-center">
              <MapPin className="h-4 w-4 mr-2" />
              Zones ({Object.keys(zones).length})
            </h5>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(zones).map(([zoneName, zone]) => (
                <div key={zoneName} className="p-2 border rounded">
                  <div className="font-medium text-sm">{zoneName}</div>
                  <div className="text-sm text-gray-600">
                    {zone.type === 'multiplier' ? `${zone.value}x` : `+₹${zone.value}`} 
                    (Base: ₹{zone.baseFare})
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cancellation Policy */}
      <div className="card">
        <div className="card-header">
          <h5 className="text-md font-medium text-gray-900 flex items-center">
            <Settings className="h-4 w-4 mr-2" />
            Cancellation Policy
          </h5>
        </div>
        <div className="card-body">
          <div className="space-y-2">
            <div className="text-sm">
              <span className="text-gray-500">Free cancellation window:</span>
              <span className="ml-1 font-medium">{cancellationPolicy.freeCancellationWindow} minutes</span>
            </div>
            {cancellationPolicy.cancellationFees?.length > 0 && (
              <div>
                <div className="text-sm text-gray-500 mb-1">Cancellation fees:</div>
                <div className="space-y-1">
                  {cancellationPolicy.cancellationFees.map((fee, index) => (
                    <div key={index} className="text-sm text-gray-600">
                      {fee.timeWindow ? `After ${fee.timeWindow} minutes:` : 'Default:'} ₹{fee.fee}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rounding Rules */}
      <div className="card">
        <div className="card-header">
          <h5 className="text-md font-medium text-gray-900 flex items-center">
            <Clock className="h-4 w-4 mr-2" />
            Rounding Rules
          </h5>
        </div>
        <div className="card-body">
          <div className="text-sm text-gray-600">
            <span className="text-gray-500">Strategy:</span>
            <span className="ml-1 font-medium capitalize">{formData.roundingRules?.strategy || 'nearest'}</span>
            <span className="mx-2">•</span>
            <span className="text-gray-500">Decimal places:</span>
            <span className="ml-1 font-medium">{formData.roundingRules?.decimalPlaces || 2}</span>
          </div>
        </div>
      </div>

      {/* Due Repay Cycle */}
      <div className="card">
        <div className="card-header">
          <h5 className="text-md font-medium text-gray-900 flex items-center">
            <Clock className="h-4 w-4 mr-2" />
            Due Repay Cycle
          </h5>
        </div>
        <div className="card-body">
          <div className="text-sm text-gray-600">
            <span className="text-gray-500">Cycle:</span>
            <span className="ml-1 font-medium capitalize">
              {formData.dueRepayCycle?.type === 'day' && `Every ${formData.dueRepayCycle?.value} days`}
              {formData.dueRepayCycle?.type === 'weekly' && `Every ${formData.dueRepayCycle?.value}`}
              {formData.dueRepayCycle?.type === 'monthly' && `Day ${formData.dueRepayCycle?.value} of every month`}
            </span>
          </div>
        </div>
      </div>

      {/* Trip Accept Duration */}
      <div className="card">
        <div className="card-header">
          <h5 className="text-md font-medium text-gray-900 flex items-center">
            <Clock className="h-4 w-4 mr-2" />
            Trip Accept Duration
          </h5>
        </div>
        <div className="card-body">
          <div className="text-sm text-gray-600">
            <span className="text-gray-500">Duration:</span>
            <span className="ml-1 font-medium">{formData.tripAcceptDuration || 30} seconds</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step6Review;
